import { useIntl, type UserUKselfCategorisationUserProvidedValues } from '@capsicum/common';
import { RadioButtonInput, TextInput } from '@capsicum/common-web';
import {
	type UkSelfCategorisationFormProps,
	type UkSelfCategorisationHighNetWorthInvestorFormType,
	useUkSelfCategorisationHighNetWorthInvestor
} from '@capsicum/features/regulations/shared';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { UserInformationForm } from './UserInformationForm';

export const HighNetWorthInvestorForm = ({
	onFormValidChange,
	onFormValuesChange,
	requirement
}: UkSelfCategorisationFormProps) => {
	const [userInformation, setUserInformation] = useState<null | UserUKselfCategorisationUserProvidedValues>(null);
	const handleUserInformationChange = (values: UserUKselfCategorisationUserProvidedValues) => {
		setUserInformation(values);
	};

	const { $t } = useIntl();

	const {
		highActualIncomeValue,
		highAnnualIncomeChoice,
		highAnnualIncomeChoiceOptions,
		isHighNetWorthFormValid,
		netAssetsChoice,
		netAssetsChoiceOptions,
		netAssetsValue,
		reset,
		resetHighNetWorthChoice,
		resetOptions,
		setUserHighActualAnnualIncome,
		setUserNetAssets,
		ukSelfCategorisationHighNetWorthInvestorForm
	} = useUkSelfCategorisationHighNetWorthInvestor();

	useEffect(() => {
		onFormValidChange(isHighNetWorthFormValid);
	}, [isHighNetWorthFormValid, onFormValidChange]);

	useEffect(() => {
		const newRestrictedFormValues: UserUKselfCategorisationUserProvidedValues = {
			category: 'high_net_worth',
			firstName: userInformation?.firstName,
			highActualAnnualIncome: parseInt(highActualIncomeValue),
			highActualNetAssets: parseInt(netAssetsValue),
			highAnnualIncomeChoice: reset ? false : highAnnualIncomeChoice,
			highNetAssetsChoice: reset ? false : netAssetsChoice,
			lastName: userInformation?.lastName
		};

		onFormValuesChange(newRestrictedFormValues);
	}, [
		highActualIncomeValue,
		highAnnualIncomeChoice,
		netAssetsChoice,
		netAssetsValue,
		onFormValuesChange,
		reset,
		ukSelfCategorisationHighNetWorthInvestorForm,
		userInformation
	]);

	return (
		<FormProvider {...ukSelfCategorisationHighNetWorthInvestorForm}>
			<S.Intro>
				<S.Description>{$t('domesticRinggitBorrowing.selectedLabel')}</S.Description>
				<S.DescriptionBold>
					{$t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthInvestorTitlePage')}
				</S.DescriptionBold>
			</S.Intro>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthInvestorText1')}</S.Paragraph>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthInvestorText2')}</S.Paragraph>
			<S.RadioButtonsContainer>
				{highAnnualIncomeChoiceOptions.map(item => (
					<RadioButtonInput
						checked={item.id === highAnnualIncomeChoice}
						item={item}
						key={item.id.toString()}
						pressableLabels
						testID={item.id ? 'high-annual-income' : 'not-high-annual-income'}
						onPress={() => setUserHighActualAnnualIncome(item.id)}
					/>
				))}
			</S.RadioButtonsContainer>
			<TextInput<UkSelfCategorisationHighNetWorthInvestorFormType>
				disabled={!highAnnualIncomeChoice}
				label={$t('jurisdictionRequirements.ukSelfCategorisation.income')}
				name="highActualAnnualIncome"
				testID="high-net-worth-investor-income-input"
				type="text"
			/>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthInvestorText3')}</S.Paragraph>
			<S.RadioButtonsContainer>
				{netAssetsChoiceOptions.map(item => (
					<RadioButtonInput
						checked={item.id === netAssetsChoice}
						item={item}
						key={item.id.toString()}
						pressableLabels
						testID={item.id ? 'high-net-assets' : 'not-high-net-assets'}
						onPress={() => setUserNetAssets(item.id)}
					/>
				))}
			</S.RadioButtonsContainer>
			<TextInput<UkSelfCategorisationHighNetWorthInvestorFormType>
				disabled={!netAssetsChoice}
				label={$t('jurisdictionRequirements.ukSelfCategorisation.netAssets')}
				name="highActualNetAssets"
				testID="high-net-worth-investor-net-assets-input"
				type="text"
			/>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.or')}</S.Paragraph>
			<S.Paragraph>
				{$t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthInvestorDoesNotApply')}
			</S.Paragraph>
			{resetOptions.map(item => (
				<RadioButtonInput
					checked={item.id === reset}
					item={item}
					key={item.id.toString()}
					pressableLabels
					testID="high-net-worth-investor-does-not-apply"
					onPress={() => resetHighNetWorthChoice(item.id)}
				/>
			))}
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthInvestorText4')}</S.Paragraph>
			<UserInformationForm requirement={requirement} onUserInformationFormChange={handleUserInformationChange} />
		</FormProvider>
	);
};

namespace S {
	export const Intro = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const Paragraph = styled.p`
		${({ theme }) => theme.fonts.text.text3};
	`;

	export const RadioButtonsContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
		margin: ${({ theme }) => `${theme.spacings.small} 0`};
	`;

	export const Description = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const DescriptionBold = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		display: block;
	`;
}
